import React, { useState } from 'react';

interface MonthlyTrainingData {
  category: string;
  yearPlan: {
    classes: number;
    students: number;
  };
  monthPlan: {
    classes: number;
    students: number;
  };
  monthImplementation: {
    classes: number;
    students: number;
  };
  monthPerformance: {
    classesPercent: string;
    studentsPercent: string;
  };
  cumulativeImplementation: {
    classes: number;
    students: number;
  };
  cumulativeVsYearPlan: {
    classesPercent: string;
    studentsPercent: string;
  };
  cumulativePreviousYear: {
    classes: number;
    students: number;
  };
  cumulativeVsPreviousYear: {
    classesPercent: string;
    studentsPercent: string;
  };
}

const MonthlyTrainingReport: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<string>('02');
  const [selectedYear, setSelectedYear] = useState<string>('2025');
  const [exportFormat, setExportFormat] = useState<string>('excel');

  const reportData: MonthlyTrainingData[] = [
    {
      category: 'Đào tạo nội bộ',
      yearPlan: { classes: 6027, students: 52300 },
      monthPlan: { classes: 176, students: 1469 },
      monthImplementation: { classes: 248, students: 2236 },
      monthPerformance: { classesPercent: '141%', studentsPercent: '153%' },
      cumulativeImplementation: { classes: 555, students: 5819 },
      cumulativeVsYearPlan: { classesPercent: '9%', studentsPercent: '11%' },
      cumulativePreviousYear: { classes: 696, students: 6846 },
      cumulativeVsPreviousYear: { classesPercent: '80%', studentsPercent: '85%' }
    },
    {
      category: 'Đào tạo VNA',
      yearPlan: { classes: 289, students: 3556 },
      monthPlan: { classes: 50, students: 730 },
      monthImplementation: { classes: 60, students: 817 },
      monthPerformance: { classesPercent: '120%', studentsPercent: '112%' },
      cumulativeImplementation: { classes: 99, students: 1204 },
      cumulativeVsYearPlan: { classesPercent: '34%', studentsPercent: '34%' },
      cumulativePreviousYear: { classes: 117, students: 1373 },
      cumulativeVsPreviousYear: { classesPercent: '85%', studentsPercent: '88%' }
    },
    {
      category: 'Đào tạo đối tác',
      yearPlan: { classes: 602, students: 7508 },
      monthPlan: { classes: 6, students: 81 },
      monthImplementation: { classes: 34, students: 436 },
      monthPerformance: { classesPercent: '617%', studentsPercent: '538%' },
      cumulativeImplementation: { classes: 37, students: 526 },
      cumulativeVsYearPlan: { classesPercent: '6%', studentsPercent: '7%' },
      cumulativePreviousYear: { classes: 40, students: 347 },
      cumulativeVsPreviousYear: { classesPercent: '93%', studentsPercent: '152%' }
    }
  ];

  // Calculate totals
  const totals: MonthlyTrainingData = {
    category: 'Tổng',
    yearPlan: {
      classes: reportData.reduce((sum, item) => sum + item.yearPlan.classes, 0),
      students: reportData.reduce((sum, item) => sum + item.yearPlan.students, 0)
    },
    monthPlan: {
      classes: reportData.reduce((sum, item) => sum + item.monthPlan.classes, 0),
      students: reportData.reduce((sum, item) => sum + item.monthPlan.students, 0)
    },
    monthImplementation: {
      classes: reportData.reduce((sum, item) => sum + item.monthImplementation.classes, 0),
      students: reportData.reduce((sum, item) => sum + item.monthImplementation.students, 0)
    },
    monthPerformance: { classesPercent: '148%', studentsPercent: '153%' },
    cumulativeImplementation: {
      classes: reportData.reduce((sum, item) => sum + item.cumulativeImplementation.classes, 0),
      students: reportData.reduce((sum, item) => sum + item.cumulativeImplementation.students, 0)
    },
    cumulativeVsYearPlan: { classesPercent: '10%', studentsPercent: '12%' },
    cumulativePreviousYear: {
      classes: reportData.reduce((sum, item) => sum + item.cumulativePreviousYear.classes, 0),
      students: reportData.reduce((sum, item) => sum + item.cumulativePreviousYear.students, 0)
    },
    cumulativeVsPreviousYear: { classesPercent: '81%', studentsPercent: '88%' }
  };

  const allData = [...reportData, totals];

  const handleExport = () => {
    console.log(`Exporting monthly report in ${exportFormat} format`);
  };

  const getPerformanceColor = (percent: string) => {
    if (!percent) return 'text-gray-400';
    const numPercent = parseInt(percent.replace('%', ''));
    if (numPercent >= 100) return 'text-green-600';
    if (numPercent >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Báo cáo tháng {selectedMonth}/{selectedYear}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            (Đính kèm báo cáo: /VIAGS-TTDT ngày __ tháng __ năm {selectedYear})
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select 
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            {Array.from({length: 12}, (_, i) => (
              <option key={i+1} value={String(i+1).padStart(2, '0')}>
                Tháng {String(i+1).padStart(2, '0')}
              </option>
            ))}
          </select>
          <select 
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="2025">2025</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
          </select>
          <select 
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="excel">Excel</option>
            <option value="pdf">PDF</option>
            <option value="csv">CSV</option>
          </select>
          <button 
            onClick={handleExport}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Xuất báo cáo
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Hiệu suất tháng {selectedMonth}/{selectedYear}</h3>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              Lớp học: <span className="font-medium text-green-600">148% kế hoạch tháng</span>
            </p>
            <p className="text-sm text-gray-600">
              Học sinh: <span className="font-medium text-green-600">153% kế hoạch tháng</span>
            </p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Lũy kế đến {selectedMonth}/{selectedYear}</h3>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              Tổng lớp: <span className="font-medium text-blue-600">691 lớp</span>
            </p>
            <p className="text-sm text-gray-600">
              Tổng học sinh: <span className="font-medium text-blue-600">7,549 người</span>
            </p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">So với cùng kỳ năm trước</h3>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              Lớp học: <span className="font-medium text-yellow-600">81% cùng kỳ</span>
            </p>
            <p className="text-sm text-gray-600">
              Học sinh: <span className="font-medium text-yellow-600">88% cùng kỳ</span>
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            {/* Complex Header */}
            <thead className="bg-blue-50">
              {/* First Header Row */}
              <tr className="border-b border-gray-300">
                <th rowSpan={2} className="px-3 py-3 text-center text-xs font-medium text-gray-700 border border-gray-300 bg-blue-100">
                  Số liệu đào tạo
                </th>
                <th colSpan={2} className="px-3 py-3 text-center text-xs font-medium text-gray-700 border border-gray-300">
                  KH được giao {selectedYear}
                </th>
                <th colSpan={2} className="px-3 py-3 text-center text-xs font-medium text-gray-700 border border-gray-300">
                  KH tháng {selectedMonth}/{selectedYear}
                </th>
                <th colSpan={2} className="px-3 py-3 text-center text-xs font-medium text-gray-700 border border-gray-300">
                  TH tháng {selectedMonth}/{selectedYear}
                </th>
                <th colSpan={2} className="px-3 py-3 text-center text-xs font-medium text-gray-700 border border-gray-300">
                  % TH theo KH
                </th>
                <th colSpan={2} className="px-3 py-3 text-center text-xs font-medium text-gray-700 border border-gray-300">
                  Lũy kế TH đến T{selectedMonth}/{selectedYear}
                </th>
                <th colSpan={2} className="px-3 py-3 text-center text-xs font-medium text-gray-700 border border-gray-300">
                  % TH so với KH {selectedYear}
                </th>
                <th colSpan={2} className="px-3 py-3 text-center text-xs font-medium text-gray-700 border border-gray-300">
                  Lũy kế TH đến T{selectedMonth}/{parseInt(selectedYear)-1}
                </th>
                <th colSpan={2} className="px-3 py-3 text-center text-xs font-medium text-gray-700 border border-gray-300">
                  % lũy kế TH so với cùng kỳ
                </th>
              </tr>
              {/* Second Header Row */}
              <tr className="border-b border-gray-300">
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">Số lớp</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">SL học sinh</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">Số lớp</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">SL học sinh</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">Số lớp</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">SL học sinh</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">Số lớp</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">SL học sinh</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">Số lớp</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">SL học sinh</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">Số lớp</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">SL học sinh</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">Số lớp</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">SL học sinh</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">Số lớp</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 border border-gray-300">SL học sinh</th>
              </tr>
            </thead>
            
            {/* Table Body */}
            <tbody className="bg-white">
              {allData.map((row, index) => (
                <tr key={row.category} className={`${row.category === 'Tổng' ? 'bg-yellow-50 font-medium' : 'hover:bg-gray-50'}`}>
                  <td className={`px-3 py-2 text-left font-medium text-gray-900 border border-gray-300 ${row.category === 'Tổng' ? 'bg-yellow-100' : ''}`}>
                    {row.category}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.yearPlan.classes.toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.yearPlan.students.toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.monthPlan.classes.toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.monthPlan.students.toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.monthImplementation.classes.toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.monthImplementation.students.toLocaleString()}
                  </td>
                  <td className={`px-2 py-2 text-center text-sm font-medium border border-gray-300 ${getPerformanceColor(row.monthPerformance.classesPercent)}`}>
                    {row.monthPerformance.classesPercent}
                  </td>
                  <td className={`px-2 py-2 text-center text-sm font-medium border border-gray-300 ${getPerformanceColor(row.monthPerformance.studentsPercent)}`}>
                    {row.monthPerformance.studentsPercent}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.cumulativeImplementation.classes.toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.cumulativeImplementation.students.toLocaleString()}
                  </td>
                  <td className={`px-2 py-2 text-center text-sm font-medium border border-gray-300 ${getPerformanceColor(row.cumulativeVsYearPlan.classesPercent)}`}>
                    {row.cumulativeVsYearPlan.classesPercent}
                  </td>
                  <td className={`px-2 py-2 text-center text-sm font-medium border border-gray-300 ${getPerformanceColor(row.cumulativeVsYearPlan.studentsPercent)}`}>
                    {row.cumulativeVsYearPlan.studentsPercent}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.cumulativePreviousYear.classes.toLocaleString()}
                  </td>
                  <td className="px-2 py-2 text-center text-sm text-gray-900 border border-gray-300">
                    {row.cumulativePreviousYear.students.toLocaleString()}
                  </td>
                  <td className={`px-2 py-2 text-center text-sm font-medium border border-gray-300 ${getPerformanceColor(row.cumulativeVsPreviousYear.classesPercent)}`}>
                    {row.cumulativeVsPreviousYear.classesPercent}
                  </td>
                  <td className={`px-2 py-2 text-center text-sm font-medium border border-gray-300 ${getPerformanceColor(row.cumulativeVsPreviousYear.studentsPercent)}`}>
                    {row.cumulativeVsPreviousYear.studentsPercent}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MonthlyTrainingReport; 